import type { CategorySlug } from 'src/common/boatCategories';
import boatCategories from 'src/common/boatCategories';
import type { Breadcrumb, Destination } from 'src/common/types/api/v4/boats/search';
import { DEFAULT_SLUG } from 'src/search/constants';
import { LandingResults } from 'src/common/types/api/v4/search/v1/landing';
import formatLocationName from './formatLocationName';

export type PageDataFromBreadCrumbs = {
  primarySlug: CategorySlug | typeof DEFAULT_SLUG;
  region?: Pick<Breadcrumb, 'name' | 'slug'>;
  country?: Pick<Breadcrumb, 'name' | 'slug'>;
  locationSlug?: Breadcrumb['slug'];
};

export const isValidCategorySlug = (
  slug: string | null | undefined,
): slug is CategorySlug | typeof DEFAULT_SLUG => (
  Object.values(boatCategories).includes(slug as CategorySlug) || slug === DEFAULT_SLUG
);

const formatLocation = (location: Breadcrumb) => ({
  name: formatLocationName(location.name),
  slug: location.slug,
});

const getPageDataFromBreadCrumbs = (
  breadcrumbs: LandingResults['breadcrumbs'],
  slug: CategorySlug | null | undefined,
  destinations: Destination[] | undefined,
  isNearbyLocationSearch: boolean,
): PageDataFromBreadCrumbs => {
  const primarySlug = slug ?? DEFAULT_SLUG;
  const defaultValues = {
    region: undefined,
    country: undefined,
  };
  const { region, country } = (() => {
    if (isNearbyLocationSearch && destinations?.length) {
      // If we are on 'Nearby' page, we should get the country from the first destination breadcrumb
      return {
        region: undefined,
        country: formatLocation(destinations[0].breadcrumbs[0]),
      };
    }
    if (breadcrumbs?.length) {
      const localLocation = breadcrumbs[breadcrumbs.length - 1];
      const broadLocation = breadcrumbs[0];
      return {
        region: formatLocation(localLocation),
        country: formatLocation(broadLocation),
      };
    }
    return defaultValues;
  })();

  const locationSlug = region?.slug ?? country?.slug;

  return {
    primarySlug,
    region,
    country,
    locationSlug,
  };
};

export default getPageDataFromBreadCrumbs;
